import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faCalendarAlt, 
  faClock, 
  faRepeat, 
  faCalendarDay 
} from '@fortawesome/free-solid-svg-icons';

interface WarehouseTaskFormProps {
  onSubmit: (data: any) => void;
  initialData?: any;
  isEdit?: boolean;
}

interface FormData {
  event_name: string;
  description: string;
  task_type: string;
  notes: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  status: string;
  is_recurring: boolean;
  recurrence_pattern: string;
  custom_recurrence_days: number | null;
  recurrence_end_date: Date | null;
}

const TASK_TYPES = [
  { value: 'maintenance', label: 'תחזוקה' },
  { value: 'inspection', label: 'בדיקה תקופתית' },
  { value: 'testing', label: 'בדיקה/טסט' },
  { value: 'cleaning', label: 'ניקוי' },
  { value: 'calibration', label: 'כיול' },
  { value: 'inventory_check', label: 'בדיקת מלאי' },
  { value: 'safety_check', label: 'בדיקת בטיחות' },
  { value: 'other', label: 'אחר' }
];

const RECURRENCE_PATTERNS = [
  { value: 'none', label: 'לא מחזורי' },
  { value: 'daily', label: 'יומי' },
  { value: 'weekly', label: 'שבועי' },
  { value: 'monthly', label: 'חודשי' },
  { value: 'quarterly', label: 'רבעוני' },
  { value: 'biannual', label: 'חצי שנתי' },
  { value: 'annual', label: 'שנתי' },
  { value: 'custom', label: 'מותאם אישית' }
];

const WarehouseTaskForm: React.FC<WarehouseTaskFormProps> = ({
  onSubmit,
  initialData,
  isEdit = false
}) => {
  const [formData, setFormData] = useState<FormData>({
    event_name: '',
    description: '',
    task_type: 'maintenance', // ברירת מחדל מוגדרת
    notes: '',
    start_date: new Date().toISOString().split('T')[0], // ברירת מחדל: היום הנוכחי
    start_time: '00:00', // ברירת מחדל: כל השעות
    end_date: new Date().toISOString().split('T')[0], // ברירת מחדל: היום הנוכחי
    end_time: '23:59', // ברירת מחדל: כל השעות
    status: 'planned',
    is_recurring: false,
    recurrence_pattern: 'none',
    custom_recurrence_days: null,
    recurrence_end_date: null as Date | null
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (initialData) {
      setFormData({
        event_name: initialData.event_name || '',
        description: initialData.description || '',
        task_type: initialData.task_type || 'maintenance',
        notes: initialData.notes || '',
        start_date: initialData.start_date ? initialData.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        start_time: initialData.start_time || '00:00',
        end_date: initialData.end_date ? initialData.end_date.split('T')[0] : new Date().toISOString().split('T')[0],
        end_time: initialData.end_time || '23:59',
        status: initialData.status || 'planned',
        is_recurring: initialData.is_recurring || false,
        recurrence_pattern: initialData.recurrence_pattern || 'none',
        custom_recurrence_days: initialData.custom_recurrence_days || null,
        recurrence_end_date: initialData.recurrence_end_date ? new Date(initialData.recurrence_end_date) : null
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.event_name.trim()) {
      newErrors.event_name = 'שם המשימה הוא שדה חובה';
    }

    if (!formData.task_type) {
      newErrors.task_type = 'סוג המשימה הוא שדה חובה';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'תאריך התחלה הוא שדה חובה';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'תאריך סיום הוא שדה חובה';
    }

    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      newErrors.end_date = 'תאריך הסיום חייב להיות אחרי תאריך ההתחלה';
    }

    // אם באותו יום – ודא ששעת סיום לא לפני שעת התחלה
    if (formData.start_date && formData.end_date && formData.start_date === formData.end_date) {
      if (formData.start_time && formData.end_time && formData.end_time < formData.start_time) {
        newErrors.end_time = 'שעת הסיום לא יכולה להיות לפני שעת ההתחלה';
      }
    }

    if (formData.is_recurring) {
      if (formData.recurrence_pattern === 'custom' && !formData.custom_recurrence_days) {
        newErrors.custom_recurrence_days = 'יש להזין מספר ימים בין מחזורים';
      }

      if (!formData.recurrence_end_date) {
        newErrors.recurrence_end_date = 'תאריך סיום המחזוריות הוא שדה חובה';
      } else if (formData.recurrence_end_date <= new Date(formData.start_date)) {
        newErrors.recurrence_end_date = 'תאריך סיום המחזוריות חייב להיות אחרי תאריך ההתחלה';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted!'); // לוג לבדיקה
    console.log('Form data:', formData); // לוג לבדיקה
    
    // בדיקת תקינות
    if (!validateForm()) {
      console.log('Validation failed, errors:', errors); // לוג לבדיקה
      return;
    }
    
    console.log('Validation passed, preparing data...'); // לוג לבדיקה
    
    // יצירת מועד מלא מתאריך ושעה
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
    
    console.log('Start datetime:', startDateTime); // לוג לבדיקה
    console.log('End datetime:', endDateTime); // לוג לבדיקה
    
    // הכנת הנתונים לשליחה (ללא שדות start_time ו-end_time)
    const submitData = {
      event_name: formData.event_name,
      description: formData.description,
      task_type: formData.task_type,
      notes: formData.notes,
      start_date: startDateTime.toISOString(),
      end_date: endDateTime.toISOString(),
      status: formData.status || 'planned',
      is_warehouse_task: true, // סימון שזה אירוע מחסן
      is_recurring: formData.is_recurring,
      recurrence_pattern: formData.recurrence_pattern,
      custom_recurrence_days: formData.custom_recurrence_days || null,
      recurrence_end_date: formData.recurrence_end_date ? formData.recurrence_end_date.toISOString().split('T')[0] : null
    };
    
    console.log('Submitting data:', submitData); // לוג לבדיקה
    console.log('Recurrence details:', {
      is_recurring: formData.is_recurring,
      pattern: formData.recurrence_pattern,
      custom_days: formData.custom_recurrence_days,
      end_date: formData.recurrence_end_date
    });
    console.log('Calling onSubmit function...'); // לוג לבדיקה
    
    try {
      await onSubmit(submitData);
      setMessage(isEdit ? 'משימה עודכנה בהצלחה' : 'משימה נוצרה בהצלחה');
      setMessageType('success');
      // ניקוי הטופס אחרי הצלחה (רק אם זה יצירה חדשה)
      if (!isEdit) {
        setTimeout(() => {
          setMessage('');
          setFormData({
            event_name: '',
            description: '',
            task_type: 'maintenance',
            notes: '',
            start_date: new Date().toISOString().split('T')[0],
            start_time: '00:00',
            end_date: new Date().toISOString().split('T')[0],
            end_time: '23:59',
            status: 'planned',
            is_recurring: false,
            recurrence_pattern: 'none',
            custom_recurrence_days: null,
            recurrence_end_date: null
          });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error in form submission:', error);
      setMessage('שגיאה ' + (isEdit ? 'בעדכון' : 'ביצירת') + ' המשימה: ' + (error.response?.data?.error || error.message || 'שגיאה לא ידועה'));
      setMessageType('error');
    }
    
    console.log('onSubmit function called successfully'); // לוג לבדיקה
  };

  const handleInputChange = (field: string, value: any) => {
    console.log('Input change:', field, value); // לוג לבדיקה
    setFormData(prev => {
      const next = { ...prev, [field]: value } as FormData;
      // תיקון אוטומטי: אם end_date קטן מ-start_date – השווה
      if (field === 'start_date' && next.end_date && next.end_date < value) {
        next.end_date = value;
      }
      if (field === 'end_date' && value && prev.start_date && value < prev.start_date) {
        next.end_date = prev.start_date;
      }
      // אם אותו יום – ודא שעת סיום לא לפני שעת התחלה
      const sameDay = next.start_date && next.end_date && next.start_date === next.end_date;
      if (sameDay && next.start_time && next.end_time && next.end_time < next.start_time) {
        if (field === 'start_time') {
          next.end_time = value;
        } else {
          next.end_time = next.start_time;
        }
      }
      return next;
    });
    
    // ניקוי שגיאות כשהשדה מתעדכן
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRecurrenceChange = (pattern: string) => {
    console.log('Recurrence pattern changed to:', pattern); // לוג לבדיקה
    
    if (pattern === 'none') {
      // אם הדפוס הוא 'none', בטל את המחזוריות
      handleInputChange('is_recurring', false);
      handleInputChange('recurrence_pattern', 'none');
      handleInputChange('recurrence_end_date', null);
      handleInputChange('custom_recurrence_days', null);
    } else {
      // אם הדפוס הוא מחזורי, הפעל את המחזוריות
      handleInputChange('is_recurring', true);
      handleInputChange('recurrence_pattern', pattern);
    }
    
    console.log('After change - is_recurring:', !formData.is_recurring, 'pattern:', pattern); // לוג לבדיקה
  };

  const handleRecurringCheckboxChange = (checked: boolean) => {
    console.log('Recurring checkbox changed to:', checked); // לוג לבדיקה
    
    if (checked) {
      // אם מסמנים מחזוריות, הגדר דפוס ברירת מחדל
      handleInputChange('is_recurring', true);
      if (formData.recurrence_pattern === 'none') {
        handleInputChange('recurrence_pattern', 'monthly'); // ברירת מחדל
      }
    } else {
      // אם מבטלים מחזוריות, אפס את כל השדות
      handleInputChange('is_recurring', false);
      handleInputChange('recurrence_pattern', 'none');
      handleInputChange('recurrence_end_date', null);
      handleInputChange('custom_recurrence_days', null);
    }
    
    console.log('After checkbox change - is_recurring:', checked, 'pattern:', formData.recurrence_pattern); // לוג לבדיקה
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3">
      <div className="row g-3">
        {/* שם המשימה */}
        <div className="col-md-6">
          <label className="form-label">שם המשימה *</label>
          <input
            type="text"
            className={`form-control ${errors.event_name ? 'is-invalid' : ''}`}
            value={formData.event_name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('event_name', e.target.value)}
            required
          />
          {errors.event_name && <div className="invalid-feedback">{errors.event_name}</div>}
        </div>

        {/* סוג המשימה */}
        <div className="col-md-6">
          <label className="form-label">סוג המשימה *</label>
          <select
            className={`form-select ${errors.task_type ? 'is-invalid' : ''}`}
            value={formData.task_type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('task_type', e.target.value)}
            required
          >
            {TASK_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.task_type && <div className="invalid-feedback">{errors.task_type}</div>}
        </div>

        {/* תיאור */}
        <div className="col-12">
          <label className="form-label">תיאור המשימה</label>
          <textarea
            className="form-control"
            rows={3}
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('description', e.target.value)}
          />
        </div>

        {/* הערות */}
        <div className="col-12">
          <label className="form-label">הערות נוספות</label>
          <textarea
            className="form-control"
            rows={2}
            value={formData.notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('notes', e.target.value)}
          />
        </div>

        {/* תאריכי התחלה וסיום */}
        <div className="col-md-6">
          <label className="form-label">תאריך התחלה</label>
          <input
            type="date"
            className="form-control"
            value={formData.start_date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('start_date', e.target.value)}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">שעת התחלה</label>
          <input
            type="time"
            className="form-control"
            value={formData.start_time}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('start_time', e.target.value)}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">תאריך סיום</label>
          <input
            type="date"
            className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
            value={formData.end_date}
            min={formData.start_date || undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('end_date', e.target.value)}
          />
          {errors.end_date && <div className="invalid-feedback">{errors.end_date}</div>}
        </div>

        <div className="col-md-6">
          <label className="form-label">שעת סיום</label>
          <input
            type="time"
            className="form-control"
            value={formData.end_time}
            min={formData.start_date === formData.end_date ? (formData.start_time || undefined) : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('end_time', e.target.value)}
          />
          {errors.end_time && <div className="invalid-feedback d-block">{errors.end_time}</div>}
        </div>

        {/* הגדרות מחזוריות */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title">הגדרות מחזוריות</h6>
              
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRecurringCheckboxChange(e.target.checked)}
                    />
                    <label className="form-check-label">משימה מחזורית</label>
                  </div>
                </div>

                {formData.is_recurring && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">דפוס מחזוריות</label>
                      <select
                        className="form-select"
                        value={formData.recurrence_pattern}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleRecurrenceChange(e.target.value)}
                      >
                        {RECURRENCE_PATTERNS.map((pattern) => (
                          <option key={pattern.value} value={pattern.value}>
                            {pattern.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.recurrence_pattern === 'custom' && (
                      <div className="col-md-4">
                        <label className="form-label">מספר ימים בין מחזורים</label>
                        <input
                          type="number"
                          className={`form-control ${errors.custom_recurrence_days ? 'is-invalid' : ''}`}
                          value={formData.custom_recurrence_days || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('custom_recurrence_days', e.target.value)}
                        />
                        {errors.custom_recurrence_days && <div className="invalid-feedback">{errors.custom_recurrence_days}</div>}
                      </div>
                    )}

                    <div className="col-md-6">
                      <label className="form-label">תאריך סיום מחזוריות *</label>
                      <input
                        type="date"
                        className={`form-control ${errors.recurrence_end_date ? 'is-invalid' : ''}`}
                        value={formData.recurrence_end_date ? formData.recurrence_end_date.toISOString().split('T')[0] : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('recurrence_end_date', e.target.value ? new Date(e.target.value) : null)}
                        required
                      />
                      {errors.recurrence_end_date && <div className="invalid-feedback">{errors.recurrence_end_date}</div>}
                    </div>

                    {/* מידע נוסף על המחזוריות */}
                    <div className="col-12">
                      <div className="alert alert-info">
                        <strong>מידע על המחזוריות:</strong><br />
                        {formData.recurrence_pattern === 'daily' && 'המשימה תתבצע מדי יום'}
                        {formData.recurrence_pattern === 'weekly' && 'המשימה תתבצע מדי שבוע'}
                        {formData.recurrence_pattern === 'monthly' && 'המשימה תתבצע מדי חודש'}
                        {formData.recurrence_pattern === 'quarterly' && 'המשימה תתבצע כל 3 חודשים'}
                        {formData.recurrence_pattern === 'biannual' && 'המשימה תתבצע כל 6 חודשים'}
                        {formData.recurrence_pattern === 'annual' && 'המשימה תתבצע מדי שנה'}
                        {formData.recurrence_pattern === 'custom' && `המשימה תתבצע כל ${formData.custom_recurrence_days || 'X'} ימים`}
                        
                        {formData.recurrence_end_date && (
                          <div className="mt-2">
                            המחזוריות תסתיים ב: {formData.recurrence_end_date.toLocaleDateString('he-IL')}
                          </div>
                        )}
                        {!formData.recurrence_end_date && (
                          <div className="mt-2 text-danger">
                            ⚠️ חובה להגדיר תאריך סיום מחזוריות
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* כפתורי פעולה */}
        <div className="col-12">
          {/* הודעות */}
          {message && (
            <div className={`alert alert-${messageType === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`}>
              {message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage('')}
              />
            </div>
          )}
          
          {/* כפתור יצירת אירועים עתידיים למשימות מחזוריות */}
          {formData.is_recurring && formData.recurrence_pattern !== 'none' && formData.recurrence_end_date && (
            <div className="alert alert-info mb-3">
              <strong>מידע:</strong> אחרי שמירת המשימה, תוכל ליצור את כל האירועים המחזוריים העתידיים באמצעות כפתור "צור אירועים עתידיים" שיופיע ברשימת המשימות.
            </div>
          )}
          
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {isEdit ? 'עדכן משימה' : 'צור משימה'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default WarehouseTaskForm;
